import LegalLayout from "@/components/legal/LegalLayout";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function TermsPage() {
  useDocumentMeta({
    title: "Terms & Conditions",
    description:
      "The terms and conditions for buying and selling on Nerd Multiverse, the UK second-hand marketplace for collectibles, comics, gaming gear and anime memorabilia.",
    path: "/terms",
  });

  return (
    <LegalLayout title="Terms & Conditions" updated="28 September 2026">
      <section>
        <h2>1. Who we are</h2>
        <p>
          Nerd Multiverse ("we", "us", "our") operates an online marketplace
          that connects buyers and sellers of second-hand collectibles,
          comics, gaming gear, anime merchandise and related memorabilia. We
          are a marketplace platform, not the seller of the items listed on
          it — each listing is created and fulfilled by an independent
          seller, and your contract for the sale of an item is with that
          seller, not with us.
        </p>
      </section>

      <section>
        <h2>2. Accepting these terms</h2>
        <p>
          By creating an account, browsing the marketplace, or placing an
          order, you agree to these Terms & Conditions and to our{" "}
          <a href="/privacy">Privacy Policy</a>. If you do not agree, please
          do not use Nerd Multiverse.
        </p>
      </section>

      <section>
        <h2>3. Accounts</h2>
        <p>
          You must provide accurate information when creating an account and
          keep it up to date. You are responsible for keeping your login
          details secure and for all activity under your account. You must
          be at least 18 years old, or have a parent or guardian's
          permission, to create an account or make a purchase.
        </p>
      </section>

      <section>
        <h2>4. Selling on Nerd Multiverse</h2>
        <p>
          Seller accounts are subject to approval, and every listing is
          reviewed before it appears on the marketplace. By listing an item,
          you confirm that:
        </p>
        <ul>
          <li>you own the item, or have the right to sell it;</li>
          <li>
            the listing's title, description, photos and condition grading
            accurately represent the item;
          </li>
          <li>
            the item is legal to sell and does not infringe anyone's
            intellectual property, safety or trading standards obligations;
          </li>
          <li>
            you will dispatch orders promptly and package items so they
            arrive in the condition described.
          </li>
        </ul>
        <p>
          We may remove a listing, suspend a seller account, or decline a
          seller application at our discretion, including where a listing
          breaches these terms or applicable law.
        </p>
      </section>

      <section>
        <h2>5. Buying on Nerd Multiverse</h2>
        <p>
          When you place an order, you're entering into a contract directly
          with the seller for that item. Payment terms, delivery and returns
          for an order are as presented to you at checkout and in the
          listing. We do our best to ensure sellers meet the standards in
          Section 4, but we don't inspect items in person and can't
          guarantee their condition, authenticity or provenance beyond what
          the seller has represented.
        </p>
      </section>

      <section>
        <h2>6. Trademarks and franchise names</h2>
        <p>
          Many items on Nerd Multiverse are officially licensed, genuine
          second-hand goods from franchises such as Marvel, Star Wars,
          Pokémon and LEGO. Franchise, character and brand names are used
          only to describe these genuine goods accurately. Nerd Multiverse
          is an independent marketplace and is not affiliated with, endorsed
          by, or sponsored by any of the rights holders of the franchises
          represented in listings.
        </p>
      </section>

      <section>
        <h2>7. Prohibited conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>list counterfeit, stolen, recalled or unlawful items;</li>
          <li>
            attempt to circumvent our fees, review process or account
            approvals;
          </li>
          <li>
            use the marketplace to harass, defraud or mislead another user;
          </li>
          <li>
            scrape, reverse-engineer or interfere with the operation of the
            site.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Liability</h2>
        <p>
          To the fullest extent permitted by law, Nerd Multiverse is not
          liable for losses arising from a transaction between a buyer and
          seller, including disputes over an item's condition, authenticity,
          delivery or non-delivery. Nothing in these terms limits liability
          that cannot be excluded under UK law, including for death or
          personal injury caused by negligence, or for fraud.
        </p>
      </section>

      <section>
        <h2>9. Suspending or closing accounts</h2>
        <p>
          We may suspend or close an account that breaches these terms, or
          where we reasonably suspect fraud, abuse or unlawful activity. You
          can close your own account at any time by contacting us.
        </p>
      </section>

      <section>
        <h2>10. Changes to these terms</h2>
        <p>
          We may update these terms from time to time, for example as the
          marketplace's features change. We'll update the "last updated"
          date above when we do. Continuing to use Nerd Multiverse after a
          change means you accept the updated terms.
        </p>
      </section>

      <section>
        <h2>11. Governing law</h2>
        <p>
          These terms are governed by the laws of England and Wales, and any
          dispute will be subject to the exclusive jurisdiction of the
          courts of England and Wales.
        </p>
      </section>

      <section>
        <h2>12. Contact</h2>
        <p>
          Questions about these terms? Contact us at{" "}
          <a href="mailto:hello@nerdmultiverse.co.uk">
            hello@nerdmultiverse.co.uk
          </a>
          .
        </p>
      </section>
    </LegalLayout>
  );
}
